// Fábrica del QueryClient de la app. Vive acá y no en `main.tsx` para poder
// probar su conducta sin montar el árbol entero (`main.tsx` llama a
// `createRoot`, que en un test no se puede ejecutar).

import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

import { AUTH_USER_KEY } from "@/hooks/useAuth";
import { ApiError } from "@/lib/api";

/** 401 = sesión muerta. 419 = CSRF irrecuperable (tras el retry de `apiFetch`). */
function esSesionMuerta(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 419);
}

/**
 * Vaciar la sesión hace que `ProtectedRoute` redirija a /login sin recargar la
 * página ni perder la SPA.
 *
 * Va tanto en la MutationCache como en la QueryCache A PROPÓSITO: con el
 * manejador solo en mutaciones, una CONSULTA que moría con 401 dejaba al
 * usuario mirando el "Unauthenticated." crudo del backend —en inglés, dentro
 * de una interfaz en español— y un botón "Reintentar" que iba a fallar igual,
 * porque nada llevaba al login.
 *
 * `fetchCurrentUser` traduce su propio 401 a `null` antes de que la query
 * falle, así que la query de sesión nunca entra por acá y no hay ciclo.
 */
export function crearQueryClient(): QueryClient {
  const queryClient: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        if (esSesionMuerta(error)) queryClient.setQueryData(AUTH_USER_KEY, null);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        if (esSesionMuerta(error)) queryClient.setQueryData(AUTH_USER_KEY, null);
      },
    }),
  });

  return queryClient;
}
